import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppState, AppAction, Medication, CalendarEvent } from '@/types';
import { db } from '@/firebase';
import {
  collection, getDocs, doc, updateDoc,
  getDoc, setDoc, addDoc, orderBy, query
} from 'firebase/firestore';
import { Pill, Calendar, FileText, Loader2, Plus, Minus, ChevronRight, X } from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface HealthProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const MEDICAL_CATEGORIES = new Set(['medical', 'dental', 'therapy', 'specialist', 'Medical', 'Dental', 'Therapy', 'Specialist']);

type HealthTab = 'medications' | 'appointments' | 'notes';

export const Health: React.FC<HealthProps> = ({ state }) => {
  const { t } = useTranslation();
  const uid = state.currentUser?.id;
  const [activeTab, setActiveTab] = useState<HealthTab>('medications');

  // Medications state
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medsLoading, setMedsLoading] = useState(false);
  const [medsError, setMedsError] = useState<string | null>(null);
  const [updatingDose, setUpdatingDose] = useState<string | null>(null);

  // Add Medication form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingMed, setAddingMed] = useState(false);
  const [newMed, setNewMed] = useState({
    name: '',
    dosage: '',
    frequency: 'daily' as Medication['frequency'],
    times: '08:00',
    currentQuantity: 30,
    refillThreshold: 7,
    prescribedBy: '',
    notes: '',
  });

  // Appointments state
  const [appointments, setAppointments] = useState<CalendarEvent[]>([]);
  const [apptLoading, setApptLoading] = useState(false);

  // Notes state
  const [healthNotes, setHealthNotes] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Load medications
  useEffect(() => {
    if (activeTab !== 'medications' || !uid) return;
    setMedsLoading(true);
    setMedsError(null);
    const fetchMeds = async () => {
      try {
        const snap = await getDocs(collection(db, 'users', uid, 'medications'));
        const meds = snap.docs.map(d => ({ id: d.id, ...d.data() } as Medication));
        setMedications(meds);
      } catch (err) {
        setMedsError('Failed to load medications.');
        console.error(err);
      } finally {
        setMedsLoading(false);
      }
    };
    fetchMeds();
  }, [activeTab, uid]);

  // Load appointments
  useEffect(() => {
    if (activeTab !== 'appointments' || !uid) return;
    setApptLoading(true);
    const fetchAppts = async () => {
      try {
        const eventsSnap = await getDocs(
          query(collection(db, 'users', uid, 'events'), orderBy('start', 'desc'))
        );
        const medEvents = eventsSnap.docs
          .map(d => ({ id: d.id, ...d.data() } as CalendarEvent))
          .filter(e => e.category && MEDICAL_CATEGORIES.has(e.category));
        setAppointments(medEvents);
      } catch (err) {
        console.error('Failed to load appointments:', err);
      } finally {
        setApptLoading(false);
      }
    };
    fetchAppts();
  }, [activeTab, uid]);

  // Load health notes
  useEffect(() => {
    if (activeTab !== 'notes' || !uid) return;
    setNotesLoading(true);
    getDoc(doc(db, 'users', uid, 'healthData', 'notes'))
      .then(snap => {
        if (snap.exists()) setHealthNotes(snap.data()?.content ?? '');
      })
      .catch(err => console.error('Failed to load health notes:', err))
      .finally(() => setNotesLoading(false));
  }, [activeTab, uid]);

  const handleRegisterDose = async (med: Medication) => {
    if (!uid || med.currentQuantity <= 0) return;
    setUpdatingDose(med.id);
    try {
      const newQty = med.currentQuantity - 1;
      await updateDoc(doc(db, 'users', uid, 'medications', med.id), { currentQuantity: newQty });
      setMedications(prev => prev.map(m => m.id === med.id ? { ...m, currentQuantity: newQty } : m));
    } catch (err) {
      console.error('Failed to register dose:', err);
    } finally {
      setUpdatingDose(null);
    }
  };

  const handleAddMedication = async () => {
    if (!uid || !newMed.name.trim()) return;
    setAddingMed(true);
    try {
      const medData: Omit<Medication, 'id'> = {
        name: newMed.name.trim(),
        dosage: newMed.dosage.trim(),
        frequency: newMed.frequency,
        times: newMed.times.split(',').map(t => t.trim()).filter(Boolean),
        startDate: new Date().toISOString().slice(0, 10),
        endDate: null,
        currentQuantity: newMed.currentQuantity,
        refillThreshold: newMed.refillThreshold,
        prescribedBy: newMed.prescribedBy.trim() || undefined,
        notes: newMed.notes.trim() || undefined,
        scope: 'personal',
      };
      const ref = await addDoc(collection(db, 'users', uid, 'medications'), medData);
      setMedications(prev => [...prev, { id: ref.id, ...medData }]);
      setShowAddForm(false);
      setNewMed({ name: '', dosage: '', frequency: 'daily', times: '08:00', currentQuantity: 30, refillThreshold: 7, prescribedBy: '', notes: '' });
    } catch (err) {
      console.error('Failed to add medication:', err);
    } finally {
      setAddingMed(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!uid) return;
    setNotesSaving(true);
    try {
      await setDoc(doc(db, 'users', uid, 'healthData', 'notes'), { content: healthNotes, updatedAt: new Date().toISOString() });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save health notes:', err);
    } finally {
      setNotesSaving(false);
    }
  };

  const TABS: TabItem[] = [
    { id: 'medications', label: t('health.tab_medications', 'Medications'), icon: <Pill className="w-3.5 h-3.5" /> },
    { id: 'appointments', label: t('health.tab_appointments', 'Appointments'), icon: <Calendar className="w-3.5 h-3.5" /> },
    { id: 'notes', label: t('health.tab_notes', 'Notes'), icon: <FileText className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="max-w-page-narrow mx-auto p-6 pb-32 animate-in fade-in duration-500">
      <p className="text-2xs font-black text-slate-400 uppercase tracking-[0.3em] mb-8">
        {t('health.title', 'Personal Health Ledger')}
      </p>

      <TabBar
        tabs={TABS}
        activeTab={activeTab}
        onChange={id => setActiveTab(id as HealthTab)}
        className="mb-8 w-fit"
      />

      {/* Medications Tab */}
      {activeTab === 'medications' && (
        <div className="animate-in fade-in duration-300">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-full font-black text-[11px] uppercase tracking-widest hover:bg-brand-hover transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('health.add_medication', 'Add Medication')}
            </button>
          </div>

          {showAddForm && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-blue-200 dark:border-blue-800 mb-6 animate-in fade-in duration-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">{t('health.new_medication', 'New Medication')}</h3>
                <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_name', 'Name *')}</label>
                  <input
                    type="text"
                    value={newMed.name}
                    onChange={e => setNewMed(p => ({ ...p, name: e.target.value }))}
                    placeholder={t('health.med_name_placeholder', 'e.g. Ibuprofen')}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_dosage', 'Dosage')}</label>
                  <input
                    type="text"
                    value={newMed.dosage}
                    onChange={e => setNewMed(p => ({ ...p, dosage: e.target.value }))}
                    placeholder={t('health.med_dosage_placeholder', 'e.g. 500mg')}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_frequency', 'Frequency')}</label>
                  <select
                    value={newMed.frequency}
                    onChange={e => setNewMed(p => ({ ...p, frequency: e.target.value as Medication['frequency'] }))}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    <option value="daily">{t('health.freq_daily', 'Daily')}</option>
                    <option value="twice_daily">{t('health.freq_twice_daily', 'Twice Daily')}</option>
                    <option value="weekly">{t('health.freq_weekly', 'Weekly')}</option>
                    <option value="as_needed">{t('health.freq_as_needed', 'As Needed')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_times', 'Times (24h, comma-separated)')}</label>
                  <input
                    type="text"
                    value={newMed.times}
                    onChange={e => setNewMed(p => ({ ...p, times: e.target.value }))}
                    placeholder={t('health.med_times_placeholder', 'e.g. 08:00, 20:00')}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_quantity', 'Current Quantity')}</label>
                  <input
                    type="number"
                    min={0}
                    value={newMed.currentQuantity}
                    onChange={e => setNewMed(p => ({ ...p, currentQuantity: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div>
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_refill_threshold', 'Refill Alert Threshold')}</label>
                  <input
                    type="number"
                    min={0}
                    value={newMed.refillThreshold}
                    onChange={e => setNewMed(p => ({ ...p, refillThreshold: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_prescribing_doctor', 'Prescribing Doctor')}</label>
                  <input
                    type="text"
                    value={newMed.prescribedBy}
                    onChange={e => setNewMed(p => ({ ...p, prescribedBy: e.target.value }))}
                    placeholder={t('health.optional', 'Optional')}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-2xs font-black uppercase tracking-widest text-slate-400 mb-1.5">{t('health.med_notes', 'Notes')}</label>
                  <input
                    type="text"
                    value={newMed.notes}
                    onChange={e => setNewMed(p => ({ ...p, notes: e.target.value }))}
                    placeholder={t('health.optional', 'Optional')}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border-none text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-5 py-2.5 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleAddMedication}
                  disabled={addingMed || !newMed.name.trim()}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand text-white rounded-full font-black text-[11px] uppercase tracking-widest hover:bg-brand-hover transition-all disabled:opacity-60"
                >
                  {addingMed ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {addingMed ? t('common.saving', 'Saving...') : t('health.save_medication', 'Save Medication')}
                </button>
              </div>
            </div>
          )}

          {medsLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="sm" />
            </div>
          )}
          {medsError && (
            <div className="text-sm text-red-500 font-bold px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-2xl mb-4">{medsError}</div>
          )}
          {!medsLoading && medications.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Pill className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-bold uppercase tracking-wide">{t('health.no_medications', 'No medications tracked')}</p>
            </div>
          )}
          <div className="space-y-4">
            {medications.map(med => (
              <div key={med.id} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-100 dark:border-slate-800">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-base font-black text-slate-900 dark:text-white">{med.name}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{med.dosage} · {med.frequency.replace('_', ' ')}</p>
                    <p className="text-[11px] text-slate-400">Times: {med.times.join(', ')}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <div className={`text-2xs font-black uppercase px-3 py-1 rounded-full ${
                        med.currentQuantity <= med.refillThreshold
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      }`}>
                        {med.currentQuantity} {t('health.remaining', 'remaining')}
                      </div>
                      {med.currentQuantity <= med.refillThreshold && (
                        <span className="text-2xs font-black uppercase text-red-500 tracking-widest">{t('health.refill_needed', 'Refill needed')}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRegisterDose(med)}
                    disabled={updatingDose === med.id || med.currentQuantity <= 0}
                    className="flex flex-col items-center gap-1 px-4 py-3 bg-brand text-white rounded-xl hover:bg-brand-hover transition-all disabled:opacity-50 shrink-0"
                    title={t('health.register_dose', 'Register dose taken')}
                  >
                    {updatingDose === med.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Minus className="w-4 h-4" />
                    }
                    <span className="text-[8px] font-black uppercase tracking-widest">{t('health.took', 'Took')}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointments Tab */}
      {activeTab === 'appointments' && (
        <div className="animate-in fade-in duration-300">
          {apptLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="sm" />
            </div>
          )}
          {!apptLoading && appointments.length === 0 && (
            <EmptyState
              icon={<Calendar className="w-8 h-8 text-slate-500" />}
              title={t('health.no_appointments', 'No medical appointments found')}
              description={t('health.no_appointments_desc', 'Events with medical, dental, therapy, or specialist categories will appear here.')}
              className="py-16"
            />
          )}
          <div className="space-y-3">
            {appointments.map(appt => (
              <div key={appt.id} className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-100 dark:border-slate-800 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-brand">
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-slate-900 dark:text-white truncate">{appt.title}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {new Date(appt.start as string).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}
                    <span className="capitalize">{appt.category}</span>
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="animate-in fade-in duration-300">
          {notesLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            <div className="space-y-4">
              <textarea
                value={healthNotes}
                onChange={e => setHealthNotes(e.target.value)}
                rows={16}
                placeholder={t('health.notes_placeholder', "Track symptoms, medical history, doctor's instructions, allergies, or any other health notes...")}
                className="w-full p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-medium text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveNotes}
                  disabled={notesSaving}
                  className="flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-full font-black text-[11px] uppercase tracking-widest hover:bg-brand-hover transition-all disabled:opacity-60"
                >
                  {notesSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {notesSaving ? t('common.saving', 'Saving...') : t('health.save_notes', 'Save Notes')}
                </button>
                {notesSaved && (
                  <span className="text-2xs font-black uppercase text-emerald-600 tracking-widest animate-in fade-in">
                    {t('health.saved', '✓ Saved')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};